// src/utils/batch-async.ts
async function batchAsync(tasks) {
  const keys = Object.keys(tasks);
  const values = await Promise.all(keys.map((k) => tasks[k]()));
  return Object.fromEntries(keys.map((k, i) => [k, values[i]]));
}

// src/utils/milliseconds-to-time-array.ts
import { Duration } from "luxon";
function millisecondsToTimeArray(milliseconds) {
  const { days, hours, minutes, seconds } = Duration.fromObject({ milliseconds }).shiftTo(
    "days",
    "hours",
    "minutes",
    "seconds",
    "milliseconds"
  );
  return [days > 0 ? days : 0, hours > 0 ? hours : 0, minutes > 0 ? minutes : 0, seconds > 0 ? seconds : 0];
}

// src/utils/calculate-chart-ticks.ts
function calculateChartTicks(startMilliseconds, latestMilliseconds) {
  const [days, hours, minutes] = millisecondsToTimeArray(latestMilliseconds - startMilliseconds);
  const INTERVAL_DURATION_MAP = [
    { condition: days === 1, intervalToDuration: 3600 * 1e3 * 4 },
    { condition: days > 0 && days < 5, intervalToDuration: 3600 * 1e3 * 8 },
    { condition: days > 0, intervalToDuration: 3600 * 1e3 * 24 },
    { condition: hours === 1, intervalToDuration: 60 * 1e3 * 10 },
    { condition: hours > 0 && hours < 5, intervalToDuration: 1800 * 1e3 },
    { condition: hours > 0 && hours < 12, intervalToDuration: 3600 * 1e3 },
    { condition: hours > 0 && hours < 24, intervalToDuration: 3600 * 1e3 * 2 },
    { condition: hours > 0, intervalToDuration: 3600 * 1e3 },
    { condition: minutes > 0 && minutes <= 15, intervalToDuration: 60 * 1e3 },
    { condition: minutes > 0 && minutes <= 30, intervalToDuration: 60 * 1e3 * 5 },
    { condition: minutes > 0, intervalToDuration: 60 * 1e3 * 10 }
  ];
  for (const elem of INTERVAL_DURATION_MAP) {
    if (elem.condition) {
      return calculateTicks(elem.intervalToDuration, startMilliseconds, latestMilliseconds);
    }
  }
  return [];
}
function calculateTicks(intervalDuration, startMilliseconds, latestMilliseconds) {
  let millisecondsCount = startMilliseconds;
  let count = 0;
  const ticks = [];
  while (millisecondsCount < latestMilliseconds) {
    ticks.push(intervalDuration * count);
    millisecondsCount += intervalDuration;
    count += 1;
  }
  return ticks;
}

// src/utils/smooth-polyline.ts
function smoothPolyline(locations) {
  const kalmanFilter = new KalmanFilter();
  return kalmanFilter.filterAll(locations);
}
var KalmanFilter = class {
  decay;
  varianceLat = -1;
  varianceLon = -1;
  latitude = 0;
  longitude = 0;
  milliseconds = 0;
  constructor() {
    this.decay = 3;
  }
  filterAll(locations) {
    return locations.map((location) => this.filter(location));
  }
  filter({
    latitude,
    longitude,
    horizontalAccuracy: accuracy,
    verticalAccuracy,
    timestamp
  }) {
    const setAccuracy = accuracy >= 1 ? accuracy : 1;
    const milliseconds = timestamp.getTime();
    if (this.varianceLat < 0 || this.varianceLon < 0) {
      this.latitude = latitude;
      this.longitude = longitude;
      this.milliseconds = milliseconds;
      this.varianceLat = setAccuracy * setAccuracy;
      this.varianceLon = setAccuracy * setAccuracy;
    } else {
      const timeIncMs = milliseconds - this.milliseconds;
      if (timeIncMs > 0) {
        this.varianceLat += timeIncMs * this.decay * this.decay / 1e3;
        this.varianceLon += timeIncMs * this.decay * this.decay / 1e3;
        this.milliseconds = milliseconds;
      }
      const kLat = this.varianceLat / (this.varianceLat + setAccuracy * setAccuracy);
      const kLon = this.varianceLon / (this.varianceLon + setAccuracy * setAccuracy);
      this.latitude += kLat * (latitude - this.latitude);
      this.longitude += kLon * (longitude - this.longitude);
      this.varianceLat *= 1 - kLat;
      this.varianceLon *= 1 - kLon;
    }
    return {
      latitude: this.latitude,
      longitude: this.longitude,
      horizontalAccuracy: accuracy,
      verticalAccuracy,
      timestamp
    };
  }
};

// src/utils/calculate-logger-distance.ts
function calculateLoggerDistance(loggerLocations, smooth = true) {
  const filteredLocations = smoothPolyline(loggerLocations);
  const points = (smooth ? filteredLocations : loggerLocations).map(
    (location) => [location.longitude, location.latitude]
  );
  let totalDistance = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const from = points[i];
    const to = points[i + 1];
    if (from != null && to != null) {
      totalDistance += haversineDistance(from, to);
    }
  }
  return totalDistance;
}
function haversineDistance(from, to) {
  const R = 6371;
  const [lon1, lat1] = from.map((coord) => coord * Math.PI / 180);
  const [lon2, lat2] = to.map((coord) => coord * Math.PI / 180);
  const dLat = lat2 - lat1;
  const dLon = lon2 - lon1;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// src/models/product-type.ts
var ProductTypeUtil = class {
  static toUIString(type, legalMark = true) {
    if (type == null) {
      return "No Device connected";
    }
    switch (type) {
      case "sherpapak-cts" /* SHERPAPAK */:
        return legalMark ? "SherpaPak\xAE" : "SherpaPak";
      case "lungguard" /* LUNGGUARD */:
        return legalMark ? "LUNGguard\xAE" : "LUNGguard";
      case "baroguard" /* BAROGUARD */:
        return legalMark ? "BAROguard\xAE" : "BAROguard";
      case "liverguard" /* LIVERGUARD */:
        return legalMark ? "LIVERguard\xAE" : "LIVERguard";
      case "sherpapak-kts" /* KTS */:
        return legalMark ? "Kidney Transport System" : "Kidney Transport System";
      case "kidneyvault" /* KIDNEYVAULT */:
        return legalMark ? "KidneyVault\u2122" : "KidneyVault";
      case "pancreaspak" /* PANCREASPAK */:
        return "PancreasPak\u2122";
      case "vantage-point" /* VANTAGEPOINT */:
        return "VantagePoint Tracking ";
      default:
        return "SherpaPak\xAE";
    }
  }
};

// src/models/timeline-entry-type.ts
var TimelineEntryTypeUtil = class {
  /**
   * Converts a timeline entry type to a human-readable UI string.
   *
   * For entries involving devices (e.g., "Lung Placed into..."), the product name
   * is included when available. When product is undefined, "Device" is used as fallback.
   *
   * @param type - The timeline entry type to convert
   * @param product - Optional product type for device-specific strings
   * @param annotations - Optional annotations for position or cannulation details
   * @returns A formatted string representation for display
   *
   * @example
   * ```typescript
   * // With product
   * TimelineEntryTypeUtil.toUIString(TimelineEntryType.LUNG_PLACED_IN_LUNGGUARD, ProductType.BAROGUARD);
   * // => "Lung(s) Placed into BAROguard®"
   *
   * // Without product (v7 sessions)
   * TimelineEntryTypeUtil.toUIString(TimelineEntryType.LUNG_PLACED_IN_LUNGGUARD);
   * // => "Lung(s) Placed into Device"
   * ```
   */
  static toUIString(type, product, annotations = {}) {
    const productName = product ? ProductTypeUtil.toUIString(product) : "Device";
    const lungPositionTypeAnnotation = product === "baroguard" /* BAROGUARD */ && annotations?.position != null ? ` - ${LungPositionTypeUtil.toUIString(annotations.position)}` : "";
    const cannulationTypeAnnotation = annotations?.cannulation != null ? ` - ${CannulationTypeUtil.toUIString(annotations.cannulation)}` : "";
    switch (type) {
      case "START_SESSION" /* START_SESSION */:
        return "Case Started";
      case "STOP_SESSION" /* STOP_SESSION */:
        return "Case Stopped";
      case "LEAVE_TX_CENTER" /* LEAVE_TX_CENTER */:
        return "Departed Recipient Center";
      case "ARRIVAL_AT_DONOR" /* ARRIVAL_AT_DONOR */:
        return "Arrived at Donor Center";
      case "DONOR_IN_ROOM" /* DONOR_IN_ROOM */:
        return "Donor In-room";
      case "HEAD_VESSEL_CLAMP" /* HEAD_VESSEL_CLAMP */:
        return "Head Vessel Clamp/Ligate";
      case "TIME_TO_INCISION" /* TIME_TO_INCISION */:
        return "Time of Incision";
      case "ORGAN_ACCEPTED" /* ORGAN_ACCEPTED */:
        return "Organ Accepted";
      case "ORGAN_DECLINED" /* ORGAN_DECLINED */:
        return "Organ Declined";
      case "OFFER_ACKNOWLEDGED" /* OFFER_ACKNOWLEDGED */:
        return "Offer Acknowledged";
      case "PROVISIONAL_YES" /* PROVISIONAL_YES */:
        return "Provisional Yes";
      case "SURGEON_REVIEW" /* SURGEON_REVIEW */:
        return "Offer in Review";
      case "OFFER_ACCEPTED" /* OFFER_ACCEPTED */:
        return "Offer Accepted";
      case "OFFER_DECLINED" /* OFFER_DECLINED */:
        return "Offer Declined";
      case "TIME_OF_WITHDRAWAL" /* TIME_OF_WITHDRAWAL */:
        return "Time of Withdrawal";
      case "WARM_ISCHEMIC_TIME_START" /* WARM_ISCHEMIC_TIME_START */:
        return "Warm Ischemic Time Start";
      case "OBSERVATION_PERIOD_START" /* OBSERVATION_PERIOD_START */:
        return "Observation Period Start";
      case "TIME_OF_DEATH" /* TIME_OF_DEATH */:
        return "Time of Death";
      case "HEPARIN_ADMINISTERED" /* HEPARIN_ADMINISTERED */:
        return "Heparin Administered";
      case "CROSSCLAMP_AT_DONOR" /* CROSSCLAMP_AT_DONOR */:
        return "Donor Crossclamp";
      case "ORGAN_REMOVAL" /* ORGAN_REMOVAL */:
        return "Heart Removal";
      case "ORGAN_REMOVAL_LUNG" /* ORGAN_REMOVAL_LUNG */:
        return "Lung Removal";
      case "AIR_LEAK_ASSESSMENT_COMPLETE" /* AIR_LEAK_ASSESSMENT_COMPLETE */:
        return "Air Leak Assessment Complete";
      case "ORGAN_REMOVAL_LIVER" /* ORGAN_REMOVAL_LIVER */:
        return "Liver Removal";
      case "HEART_PLACED_IN_SHERPAPAK" /* HEART_PLACED_IN_SHERPAPAK */:
        return `Heart Placed into ${productName}`;
      case "LUNG_PLACED_IN_LUNGGUARD" /* LUNG_PLACED_IN_LUNGGUARD */:
        return `Lung(s) Placed into ${productName}${lungPositionTypeAnnotation}`;
      case "LIVER_PLACED_IN_LIVERGUARD" /* LIVER_PLACED_IN_LIVERGUARD */:
        return `Liver Placed into ${productName}`;
      case "LEAVE_DONOR" /* LEAVE_DONOR */:
        return "Departed Donor Center";
      case "ARRIVE_AT_RECIPIENT" /* ARRIVE_AT_RECIPIENT */:
        return "Arrived at Recipient Center";
      case "HEART_REMOVED_FROM_SHERPAPAK" /* HEART_REMOVED_FROM_SHERPAPAK */:
        return `Heart Removed from ${productName}`;
      case "LIVER_REMOVED_FROM_LIVERGUARD" /* LIVER_REMOVED_FROM_LIVERGUARD */:
        return `Liver Removed from ${productName}`;
      case "LUNGS_REMOVED_FROM_LUNGGUARD" /* LUNGS_REMOVED_FROM_LUNGGUARD */:
        return `Lungs Removed from ${productName}`;
      case "LEFT_LUNG_REMOVED_FROM_LUNGGUARD" /* LEFT_LUNG_REMOVED_FROM_LUNGGUARD */:
        return `Left Lung Removed from ${productName}`;
      case "RIGHT_LUNG_REMOVED_FROM_LUNGGUARD" /* RIGHT_LUNG_REMOVED_FROM_LUNGGUARD */:
        return `Right Lung Removed from ${productName}`;
      case "ANASTOMOSIS" /* ANASTOMOSIS */:
        return "Anastomosis";
      case "LEFT_LUNG_ANASTOMOSIS_COMPLETE" /* LEFT_LUNG_ANASTOMOSIS_COMPLETE */:
        return "Left Lung Anastomosis Complete";
      case "RIGHT_LUNG_ANASTOMOSIS_COMPLETE" /* RIGHT_LUNG_ANASTOMOSIS_COMPLETE */:
        return "Right Lung Anastomosis Complete";
      case "CROSSCLAMP_AT_RECIPIENT" /* CROSSCLAMP_AT_RECIPIENT */:
        return "Recipient Off Clamp";
      case "RECIPIENT_LEFT_LUNG_OFF_CLAMP" /* RECIPIENT_LEFT_LUNG_OFF_CLAMP */:
        return "Recipient Left Lung Off Clamp";
      case "RECIPIENT_RIGHT_LUNG_OFF_CLAMP" /* RECIPIENT_RIGHT_LUNG_OFF_CLAMP */:
        return "Recipient Right Lung Off Clamp";
      case "PORTAL_REPERFUSION" /* PORTAL_REPERFUSION */:
        return "Portal Reperfusion";
      case "ARTERIAL_REPERFUSION" /* ARTERIAL_REPERFUSION */:
        return "Arterial Reperfusion";
      case "PERFUSION_START" /* PERFUSION_START */:
        return "Start Aortic Flush";
      case "PERFUSION_STOP" /* PERFUSION_STOP */:
        return "End Aortic Flush";
      case "ON_CARDIO_PULMONARY_BYPASS_1" /* ON_CARDIO_PULMONARY_BYPASS_1 */:
        return "On Cardio Pulmonary Bypass 1";
      case "START_WEAN_1" /* START_WEAN_1 */:
        return "Start Wean 1";
      case "OFF_CARDIO_PULMONARY_BYPASS_1" /* OFF_CARDIO_PULMONARY_BYPASS_1 */:
        return "Off Cardio Pulmonary Bypass 1";
      case "ON_CARDIO_PULMONARY_BYPASS_2" /* ON_CARDIO_PULMONARY_BYPASS_2 */:
        return "On Cardio Pulmonary Bypass 2";
      case "START_WEAN_2" /* START_WEAN_2 */:
        return "Start Wean 2";
      case "OFF_CARDIO_PULMONARY_BYPASS_2" /* OFF_CARDIO_PULMONARY_BYPASS_2 */:
        return "Off Cardio Pulmonary Bypass 2";
      case "LEAVE_OPO" /* LEAVE_OPO */:
        return "Departed OPO";
      case "ARRIVE_AT_OPO" /* ARRIVE_AT_OPO */:
        return "Arrived at OPO";
      case "KIDNEY_DONOR_FINAL_BLOOD_UREA_NITROGEN" /* KIDNEY_DONOR_FINAL_BLOOD_UREA_NITROGEN */:
        return "Donor Final Blood Urea Nitrogen";
      case "KIDNEY_DONOR_FINAL_SERUM_CREATININE" /* KIDNEY_DONOR_FINAL_SERUM_CREATININE */:
        return "Donor Final Serum Creatinine";
      case "KIDNEY_DONOR_KIDNEY_DONOR_PROFILE_INDEX" /* KIDNEY_DONOR_KIDNEY_DONOR_PROFILE_INDEX */:
        return "Donor Kidney Donor Profile Index";
      case "KIDNEY_WITHDRAWAL_OF_SUPPORT" /* KIDNEY_WITHDRAWAL_OF_SUPPORT */:
        return "Withdrawal of Support";
      case "KIDNEY_START_AGONAL_PHASE" /* KIDNEY_START_AGONAL_PHASE */:
        return "Start of Agonal Phase";
      case "KIDNEY_START_FLUSH" /* KIDNEY_START_FLUSH */:
        return "Start Flush";
      case "KIDNEY_X_CLAMP" /* KIDNEY_X_CLAMP */:
        return "X Clamp";
      case "LEFT_KIDNEY_REMOVAL" /* LEFT_KIDNEY_REMOVAL */:
        return "Left Kidney Removal";
      case "RIGHT_KIDNEY_REMOVAL" /* RIGHT_KIDNEY_REMOVAL */:
        return "Right Kidney Removal";
      case "LEFT_KIDNEY_PLACED_ON_ICE" /* LEFT_KIDNEY_PLACED_ON_ICE */:
        return "Left Kidney Placed on Ice";
      case "RIGHT_KIDNEY_PLACED_ON_ICE" /* RIGHT_KIDNEY_PLACED_ON_ICE */:
        return "Right Kidney Placed on Ice";
      case "LEFT_KIDNEY_PLACED_INTO_STORAGE" /* LEFT_KIDNEY_PLACED_INTO_STORAGE */:
        return `Left Kidney Placed into ${productName}`;
      case "RIGHT_KIDNEY_PLACED_INTO_STORAGE" /* RIGHT_KIDNEY_PLACED_INTO_STORAGE */:
        return `Right Kidney Placed into ${productName}`;
      case "LEFT_KIDNEY_PLACED_IN_THIRD_PARTY_STORAGE" /* LEFT_KIDNEY_PLACED_IN_THIRD_PARTY_STORAGE */:
        return "Left Kidney Placed into Non-Integrated Perfusion Device";
      case "RIGHT_KIDNEY_PLACED_IN_THIRD_PARTY_STORAGE" /* RIGHT_KIDNEY_PLACED_IN_THIRD_PARTY_STORAGE */:
        return "Right Kidney Placed into Non-Integrated Perfusion Device";
      case "LEFT_KIDNEY_HANDED_OFF_TO_COURIER" /* LEFT_KIDNEY_HANDED_OFF_TO_COURIER */:
        return "Left Kidney Handed off to Courier/Air";
      case "RIGHT_KIDNEY_HANDED_OFF_TO_COURIER" /* RIGHT_KIDNEY_HANDED_OFF_TO_COURIER */:
        return "Right Kidney Handed off to Courier/Air";
      case "LEFT_KIDNEY_ARRIVED_AT_TXC_OR_OPO" /* LEFT_KIDNEY_ARRIVED_AT_TXC_OR_OPO */:
        return "Left Kidney Arrived at TXC or OPO";
      case "RIGHT_KIDNEY_ARRIVED_AT_TXC_OR_OPO" /* RIGHT_KIDNEY_ARRIVED_AT_TXC_OR_OPO */:
        return "Right Kidney Arrived at TXC or OPO";
      case "LEFT_KIDNEY_OUT_OF_STORAGE" /* LEFT_KIDNEY_OUT_OF_STORAGE */:
        return `Left Kidney Out of ${productName}`;
      case "RIGHT_KIDNEY_OUT_OF_STORAGE" /* RIGHT_KIDNEY_OUT_OF_STORAGE */:
        return `Right Kidney Out of ${productName}`;
      case "LEFT_KIDNEY_REMOVED_FROM_ICE" /* LEFT_KIDNEY_REMOVED_FROM_ICE */:
        return "Left Kidney Removed from Ice";
      case "RIGHT_KIDNEY_REMOVED_FROM_ICE" /* RIGHT_KIDNEY_REMOVED_FROM_ICE */:
        return "Right Kidney Removed from Ice";
      case "LEFT_KIDNEY_REMOVED_FROM_THIRD_PARTY_STORAGE" /* LEFT_KIDNEY_REMOVED_FROM_THIRD_PARTY_STORAGE */:
        return "Left Kidney Removed from Non-Integrated Perfusion Device";
      case "RIGHT_KIDNEY_REMOVED_FROM_THIRD_PARTY_STORAGE" /* RIGHT_KIDNEY_REMOVED_FROM_THIRD_PARTY_STORAGE */:
        return "Right Kidney Removed from Non-Integrated Perfusion Device";
      case "LEFT_KIDNEY_ARTERY_ANASTOMOSIS" /* LEFT_KIDNEY_ARTERY_ANASTOMOSIS */:
        return "Left Kidney Artery Anastomosis";
      case "RIGHT_KIDNEY_ARTERY_ANASTOMOSIS" /* RIGHT_KIDNEY_ARTERY_ANASTOMOSIS */:
        return "Right Kidney Artery Anastomosis";
      case "LEFT_KIDNEY_VEIN_ANASTOMOSIS" /* LEFT_KIDNEY_VEIN_ANASTOMOSIS */:
        return "Left Kidney Vein Anastomosis";
      case "RIGHT_KIDNEY_VEIN_ANASTOMOSIS" /* RIGHT_KIDNEY_VEIN_ANASTOMOSIS */:
        return "Right Kidney Vein Anastomosis";
      case "LEFT_KIDNEY_URETER_ANASTOMOSIS" /* LEFT_KIDNEY_URETER_ANASTOMOSIS */:
        return "Left Kidney Ureter Anastomosis";
      case "RIGHT_KIDNEY_URETER_ANASTOMOSIS" /* RIGHT_KIDNEY_URETER_ANASTOMOSIS */:
        return "Right Kidney Ureter Anastomosis";
      case "LEFT_KIDNEY_ACCEPTED" /* LEFT_KIDNEY_ACCEPTED */:
        return "Left Kidney Accepted for Recovery";
      case "RIGHT_KIDNEY_ACCEPTED" /* RIGHT_KIDNEY_ACCEPTED */:
        return "Right Kidney Accepted for Recovery";
      case "LEFT_KIDNEY_ALLOCATION_COMPLETE" /* LEFT_KIDNEY_ALLOCATION_COMPLETE */:
        return "Left Kidney Allocation Complete";
      case "RIGHT_KIDNEY_ALLOCATION_COMPLETE" /* RIGHT_KIDNEY_ALLOCATION_COMPLETE */:
        return "Right Kidney Allocation Complete";
      case "LEFT_KIDNEY_NOT_RECOVERED" /* LEFT_KIDNEY_NOT_RECOVERED */:
        return "Left Kidney Not Recovered";
      case "RIGHT_KIDNEY_NOT_RECOVERED" /* RIGHT_KIDNEY_NOT_RECOVERED */:
        return "Right Kidney Not Recovered";
      case "LEFT_KIDNEY_LIST_EXHAUSTED" /* LEFT_KIDNEY_LIST_EXHAUSTED */:
        return "List Exhausted (Left Kidney)";
      case "RIGHT_KIDNEY_LIST_EXHAUSTED" /* RIGHT_KIDNEY_LIST_EXHAUSTED */:
        return "List Exhausted (Right Kidney)";
      case "ORGAN_REMOVAL_PANCREAS" /* ORGAN_REMOVAL_PANCREAS */:
        return "Pancreas Removal";
      case "PANCREAS_PLACED_IN_PANCREASPAK" /* PANCREAS_PLACED_IN_PANCREASPAK */:
        return `Pancreas Placed into ${productName}`;
      case "PANCREAS_REMOVED_FROM_PANCREASPAK" /* PANCREAS_REMOVED_FROM_PANCREASPAK */:
        return `Pancreas Removed from ${productName}`;
      case "EVLP_START" /* EVLP_START */:
        return "EVLP Start";
      case "EVLP_STOP" /* EVLP_STOP */:
        return "EVLP Stop";
      case "CANNULATION" /* CANNULATION */:
        return `Cannulation${cannulationTypeAnnotation}`;
      case "COLD_PRESERVATION_FLUSH" /* COLD_PRESERVATION_FLUSH */:
        return "Cold Preservation Flush";
      case "TIMEOUT" /* TIMEOUT */:
        return "Timeout";
      case "CUSTOM" /* CUSTOM */:
        return "Custom Event";
      case "UNKNOWN" /* UNKNOWN */:
        return "Unknown Event";
      case "START_LOGGER" /* START_LOGGER */:
        return "Logger Started";
      case "STOP_LOGGER" /* STOP_LOGGER */:
        return "Logger Stopped";
      case "PLANE_DEPARTURE_RECIPIENT" /* PLANE_DEPARTURE_RECIPIENT */:
        return "Take-off at Recipient Airport";
      case "PLANE_ARRIVAL_DONOR" /* PLANE_ARRIVAL_DONOR */:
        return "Landed at Donor Airport";
      case "PLANE_DEPARTURE_DONOR" /* PLANE_DEPARTURE_DONOR */:
        return "Take-off at Donor Airport";
      case "PLANE_ARRIVAL_RECIPIENT" /* PLANE_ARRIVAL_RECIPIENT */:
        return "Landed at Recipient Airport";
      case "LEFT_KIDNEY_DECLINED" /* LEFT_KIDNEY_DECLINED */:
        return "Left Kidney Declined";
      case "RIGHT_KIDNEY_DECLINED" /* RIGHT_KIDNEY_DECLINED */:
        return "Right Kidney Declined";
      default:
        return "Unknown Event";
    }
  }
  static getOrganPosition(type) {
    const byPositions = {
      ["LEFT" /* LEFT */]: [
        "LEFT_LUNG_REMOVED_FROM_LUNGGUARD" /* LEFT_LUNG_REMOVED_FROM_LUNGGUARD */,
        "LEFT_LUNG_ANASTOMOSIS_COMPLETE" /* LEFT_LUNG_ANASTOMOSIS_COMPLETE */,
        "RECIPIENT_LEFT_LUNG_OFF_CLAMP" /* RECIPIENT_LEFT_LUNG_OFF_CLAMP */,
        "LEFT_KIDNEY_REMOVAL" /* LEFT_KIDNEY_REMOVAL */,
        "LEFT_KIDNEY_PLACED_ON_ICE" /* LEFT_KIDNEY_PLACED_ON_ICE */,
        "LEFT_KIDNEY_PLACED_INTO_STORAGE" /* LEFT_KIDNEY_PLACED_INTO_STORAGE */,
        "LEFT_KIDNEY_PLACED_IN_THIRD_PARTY_STORAGE" /* LEFT_KIDNEY_PLACED_IN_THIRD_PARTY_STORAGE */,
        "LEFT_KIDNEY_HANDED_OFF_TO_COURIER" /* LEFT_KIDNEY_HANDED_OFF_TO_COURIER */,
        "LEFT_KIDNEY_ARRIVED_AT_TXC_OR_OPO" /* LEFT_KIDNEY_ARRIVED_AT_TXC_OR_OPO */,
        "LEFT_KIDNEY_OUT_OF_STORAGE" /* LEFT_KIDNEY_OUT_OF_STORAGE */,
        "LEFT_KIDNEY_REMOVED_FROM_ICE" /* LEFT_KIDNEY_REMOVED_FROM_ICE */,
        "LEFT_KIDNEY_REMOVED_FROM_THIRD_PARTY_STORAGE" /* LEFT_KIDNEY_REMOVED_FROM_THIRD_PARTY_STORAGE */,
        "LEFT_KIDNEY_ARTERY_ANASTOMOSIS" /* LEFT_KIDNEY_ARTERY_ANASTOMOSIS */,
        "LEFT_KIDNEY_VEIN_ANASTOMOSIS" /* LEFT_KIDNEY_VEIN_ANASTOMOSIS */,
        "LEFT_KIDNEY_URETER_ANASTOMOSIS" /* LEFT_KIDNEY_URETER_ANASTOMOSIS */,
        "LEFT_KIDNEY_ACCEPTED" /* LEFT_KIDNEY_ACCEPTED */,
        "LEFT_KIDNEY_ALLOCATION_COMPLETE" /* LEFT_KIDNEY_ALLOCATION_COMPLETE */,
        "LEFT_KIDNEY_NOT_RECOVERED" /* LEFT_KIDNEY_NOT_RECOVERED */,
        "LEFT_KIDNEY_LIST_EXHAUSTED" /* LEFT_KIDNEY_LIST_EXHAUSTED */,
        "LEFT_KIDNEY_DECLINED" /* LEFT_KIDNEY_DECLINED */
      ],
      ["RIGHT" /* RIGHT */]: [
        "RIGHT_LUNG_REMOVED_FROM_LUNGGUARD" /* RIGHT_LUNG_REMOVED_FROM_LUNGGUARD */,
        "RIGHT_LUNG_ANASTOMOSIS_COMPLETE" /* RIGHT_LUNG_ANASTOMOSIS_COMPLETE */,
        "RECIPIENT_RIGHT_LUNG_OFF_CLAMP" /* RECIPIENT_RIGHT_LUNG_OFF_CLAMP */,
        "RIGHT_KIDNEY_REMOVAL" /* RIGHT_KIDNEY_REMOVAL */,
        "RIGHT_KIDNEY_PLACED_ON_ICE" /* RIGHT_KIDNEY_PLACED_ON_ICE */,
        "RIGHT_KIDNEY_PLACED_INTO_STORAGE" /* RIGHT_KIDNEY_PLACED_INTO_STORAGE */,
        "RIGHT_KIDNEY_PLACED_IN_THIRD_PARTY_STORAGE" /* RIGHT_KIDNEY_PLACED_IN_THIRD_PARTY_STORAGE */,
        "RIGHT_KIDNEY_HANDED_OFF_TO_COURIER" /* RIGHT_KIDNEY_HANDED_OFF_TO_COURIER */,
        "RIGHT_KIDNEY_ARRIVED_AT_TXC_OR_OPO" /* RIGHT_KIDNEY_ARRIVED_AT_TXC_OR_OPO */,
        "RIGHT_KIDNEY_OUT_OF_STORAGE" /* RIGHT_KIDNEY_OUT_OF_STORAGE */,
        "RIGHT_KIDNEY_REMOVED_FROM_ICE" /* RIGHT_KIDNEY_REMOVED_FROM_ICE */,
        "RIGHT_KIDNEY_REMOVED_FROM_THIRD_PARTY_STORAGE" /* RIGHT_KIDNEY_REMOVED_FROM_THIRD_PARTY_STORAGE */,
        "RIGHT_KIDNEY_ARTERY_ANASTOMOSIS" /* RIGHT_KIDNEY_ARTERY_ANASTOMOSIS */,
        "RIGHT_KIDNEY_VEIN_ANASTOMOSIS" /* RIGHT_KIDNEY_VEIN_ANASTOMOSIS */,
        "RIGHT_KIDNEY_URETER_ANASTOMOSIS" /* RIGHT_KIDNEY_URETER_ANASTOMOSIS */,
        "RIGHT_KIDNEY_ACCEPTED" /* RIGHT_KIDNEY_ACCEPTED */,
        "RIGHT_KIDNEY_ALLOCATION_COMPLETE" /* RIGHT_KIDNEY_ALLOCATION_COMPLETE */,
        "RIGHT_KIDNEY_NOT_RECOVERED" /* RIGHT_KIDNEY_NOT_RECOVERED */,
        "RIGHT_KIDNEY_LIST_EXHAUSTED" /* RIGHT_KIDNEY_LIST_EXHAUSTED */,
        "RIGHT_KIDNEY_DECLINED" /* RIGHT_KIDNEY_DECLINED */
      ]
    };
    if (byPositions["LEFT" /* LEFT */].includes(type)) {
      return "LEFT" /* LEFT */;
    }
    if (byPositions["RIGHT" /* RIGHT */].includes(type)) {
      return "RIGHT" /* RIGHT */;
    }
    return null;
  }
  static attributes(type) {
    switch (type) {
      case "HEART_PLACED_IN_SHERPAPAK" /* HEART_PLACED_IN_SHERPAPAK */:
        return ["STARTS_LOGGER" /* STARTS_LOGGER */];
      case "LUNG_PLACED_IN_LUNGGUARD" /* LUNG_PLACED_IN_LUNGGUARD */:
        return ["STARTS_LOGGER" /* STARTS_LOGGER */];
      case "LIVER_PLACED_IN_LIVERGUARD" /* LIVER_PLACED_IN_LIVERGUARD */:
        return ["STARTS_LOGGER" /* STARTS_LOGGER */];
      case "PANCREAS_PLACED_IN_PANCREASPAK" /* PANCREAS_PLACED_IN_PANCREASPAK */:
        return ["STARTS_LOGGER" /* STARTS_LOGGER */];
      case "LEFT_KIDNEY_PLACED_INTO_STORAGE" /* LEFT_KIDNEY_PLACED_INTO_STORAGE */:
        return ["STARTS_LOGGER" /* STARTS_LOGGER */];
      case "RIGHT_KIDNEY_PLACED_INTO_STORAGE" /* RIGHT_KIDNEY_PLACED_INTO_STORAGE */:
        return ["STARTS_LOGGER" /* STARTS_LOGGER */];
      case "HEART_REMOVED_FROM_SHERPAPAK" /* HEART_REMOVED_FROM_SHERPAPAK */:
        return ["STOPS_LOGGER" /* STOPS_LOGGER */];
      case "LUNGS_REMOVED_FROM_LUNGGUARD" /* LUNGS_REMOVED_FROM_LUNGGUARD */:
        return ["STOPS_LOGGER" /* STOPS_LOGGER */];
      case "LIVER_REMOVED_FROM_LIVERGUARD" /* LIVER_REMOVED_FROM_LIVERGUARD */:
        return ["STOPS_LOGGER" /* STOPS_LOGGER */];
      case "PANCREAS_REMOVED_FROM_PANCREASPAK" /* PANCREAS_REMOVED_FROM_PANCREASPAK */:
        return ["STOPS_LOGGER" /* STOPS_LOGGER */];
      case "LEFT_LUNG_REMOVED_FROM_LUNGGUARD" /* LEFT_LUNG_REMOVED_FROM_LUNGGUARD */:
        return ["STOPS_LOGGER" /* STOPS_LOGGER */];
      case "LEFT_KIDNEY_OUT_OF_STORAGE" /* LEFT_KIDNEY_OUT_OF_STORAGE */:
        return ["STOPS_LOGGER" /* STOPS_LOGGER */];
      case "RIGHT_LUNG_REMOVED_FROM_LUNGGUARD" /* RIGHT_LUNG_REMOVED_FROM_LUNGGUARD */:
        return ["STOPS_LOGGER" /* STOPS_LOGGER */];
      case "RIGHT_KIDNEY_OUT_OF_STORAGE" /* RIGHT_KIDNEY_OUT_OF_STORAGE */:
        return ["STOPS_LOGGER" /* STOPS_LOGGER */];
      default:
        return [];
    }
  }
};

// src/models/cannulation-type.ts
var CannulationTypeUtil = class {
  static toUIString(type) {
    switch (type) {
      case "TANRP" /* TANRP */:
        return "TA-NRP";
      case "ANRP" /* ANRP */:
        return "A-NRP";
      default:
        return "unknown";
    }
  }
};

// src/models/draft-state-validation.ts
var VALID_DRAFT_STATES = {
  ["DONOR_SUMMARY_EXTRACTION" /* DONOR_SUMMARY_EXTRACTION */]: [
    "WAITING_FOR_UPLOAD" /* WAITING_FOR_UPLOAD */,
    "PROCESSING" /* PROCESSING */,
    "COMPLETE" /* COMPLETE */,
    "ERROR" /* ERROR */
  ],
  ["MANUAL" /* MANUAL */]: ["COMPLETE" /* COMPLETE */]
};
var VALID_STATE_TRANSITIONS = {
  ["WAITING_FOR_UPLOAD" /* WAITING_FOR_UPLOAD */]: ["PROCESSING" /* PROCESSING */, "ERROR" /* ERROR */],
  ["PROCESSING" /* PROCESSING */]: ["COMPLETE" /* COMPLETE */, "ERROR" /* ERROR */],
  ["COMPLETE" /* COMPLETE */]: [],
  // Terminal state - no transitions allowed
  ["ERROR" /* ERROR */]: ["WAITING_FOR_UPLOAD" /* WAITING_FOR_UPLOAD */]
  // Can retry from error
};

// src/models/lung-position.ts
var LungPositionTypeUtil = class {
  static toUIString(type) {
    switch (type) {
      case "SUPINE" /* SUPINE */:
        return "Supine";
      case "PRONE" /* PRONE */:
        return "Prone";
      default:
        return "unknown";
    }
  }
};

// src/models/meta.ts
var META_REQUIRED_FIELDS = [
  "userId",
  "deviceIdentifier",
  "timestampUser",
  "timezone",
  "language",
  "locale",
  "device",
  "os",
  "appVersion",
  "osVersion",
  "appId"
];
var META_OPTIONAL_FIELDS = ["viaCloudFunction", "cloudFunctionVersion"];
var META_ALL_FIELDS = [...META_REQUIRED_FIELDS, ...META_OPTIONAL_FIELDS];

// src/models/preservation-method.ts
var ORGAN_PRODUCTS = {
  ["heart" /* HEART */]: ["sherpapak-cts" /* SHERPAPAK */],
  ["lung" /* LUNG */]: ["lungguard" /* LUNGGUARD */, "baroguard" /* BAROGUARD */],
  ["liver" /* LIVER */]: ["liverguard" /* LIVERGUARD */],
  ["kidney" /* KIDNEY */]: ["kidneyvault" /* KIDNEYVAULT */],
  ["pancreas" /* PANCREAS */]: ["pancreaspak" /* PANCREASPAK */]
};
var ORGAN_MISC_METHODS = {
  ["heart" /* HEART */]: ["ICE" /* ICE */, "NMP" /* NMP */],
  ["lung" /* LUNG */]: ["ICE" /* ICE */, "NMP" /* NMP */],
  ["liver" /* LIVER */]: ["ICE" /* ICE */, "NMP" /* NMP */, "HOPE" /* HOPE */],
  ["kidney" /* KIDNEY */]: ["ICE" /* ICE */, "HMP" /* HMP */],
  ["pancreas" /* PANCREAS */]: ["ICE" /* ICE */]
};

// src/utils/format-duration.ts
function formatDuration(milliseconds, options) {
  if (milliseconds == null || Number.isNaN(milliseconds)) {
    return "-";
  }
  const formatter = new Intl.NumberFormat();
  if (options?.convertToUnit) {
    let value;
    const unitChar = options.convertToUnit;
    switch (options.convertToUnit) {
      case "h":
        value = Math.floor(milliseconds / (1e3 * 60 * 60));
        break;
      case "m":
        value = Math.floor(milliseconds / (1e3 * 60));
        break;
      case "s":
        value = Math.floor(milliseconds / 1e3);
        break;
      default:
        return "-";
    }
    return `${formatter.format(value)}${unitChar}`;
  }
  const [rawDays, rawHours, rawMinutes, rawSeconds] = millisecondsToTimeArray(milliseconds);
  const totalHours = rawHours + rawDays * 24;
  const activeUnits = options?.displayUnits ?? ["h", "m", "s"];
  const potentialParts = [];
  const showZeroUnits = options?.showZeroUnits;
  if (activeUnits.includes("h") && (totalHours > 0 || showZeroUnits)) {
    potentialParts.push(`${formatter.format(totalHours)}h`);
  }
  if (activeUnits.includes("m") && (rawMinutes > 0 || showZeroUnits)) {
    potentialParts.push(`${formatter.format(rawMinutes)}m`);
  }
  if (activeUnits.includes("s") && (rawSeconds > 0 || showZeroUnits)) {
    potentialParts.push(`${formatter.format(rawSeconds)}s`);
  }
  let finalParts = potentialParts;
  if (finalParts.length === 0) {
    if (milliseconds === 0) {
      if (activeUnits.includes("s")) return "0s";
      if (activeUnits.includes("m")) return "0m";
      if (activeUnits.includes("h")) return "0h";
      return "0s";
    } else {
      return "";
    }
  }
  if (options?.maxUnits != null) {
    if (options.maxUnits <= 0) {
      finalParts = [];
    } else if (options.maxUnits < finalParts.length) {
      finalParts = finalParts.slice(0, options.maxUnits);
    }
  }
  return finalParts.join(" ");
}

// src/utils/get-default-measurement-unit.ts
var METRIC_LANGUAGE_PREFIXES = [
  "de",
  "fr",
  "it",
  "es",
  "pt",
  "nl",
  "sv",
  "no",
  "nb",
  "nn",
  "da",
  "fi",
  "pl",
  "cs",
  "sk",
  "hu",
  "el",
  "ro",
  "bg",
  "hr",
  "sl",
  "lt",
  "lv",
  "et",
  "ru",
  "uk",
  "ja",
  "ko",
  "zh",
  "th",
  "vi",
  "id",
  "ms",
  "tr",
  "he",
  "ar",
  "hi",
  "bn"
];
var METRIC_EN_VARIANTS = ["en-au", "en-nz", "en-ca", "en-ie", "en-in", "en-za"];
function isMetricLocale(locale) {
  const normalized = locale.toLowerCase();
  if (METRIC_EN_VARIANTS.some((v) => normalized.startsWith(v))) return true;
  const language = normalized.split("-")[0];
  return METRIC_LANGUAGE_PREFIXES.includes(language);
}
function getDefaultMeasurementUnit(localeHint) {
  if (localeHint) {
    return isMetricLocale(localeHint) ? "metric" /* METRIC */ : "imperial" /* IMPERIAL */;
  }
  if (typeof window !== "undefined") {
    const locale = navigator.language ?? "en-US";
    return isMetricLocale(locale) ? "metric" /* METRIC */ : "imperial" /* IMPERIAL */;
  }
  return "imperial" /* IMPERIAL */;
}

// src/utils/chart-data/chart-data-point.ts
import { nanoid } from "nanoid";
var ChartDataPoint = class {
  id;
  timestamp;
  value;
  isHighlight;
  constructor(params) {
    this.id = nanoid();
    this.timestamp = params.timestamp;
    this.value = params.value;
    this.isHighlight = params.isHighlight ?? false;
  }
};

// src/utils/chart-data/chart-data-segment.ts
import { nanoid as nanoid2 } from "nanoid";
var ChartDataSegment = class {
  id;
  dataPoints;
  constructor(params) {
    this.id = nanoid2();
    this.dataPoints = params.dataPoints;
  }
  get start() {
    return this.dataPoints[0].timestamp;
  }
  get end() {
    return this.dataPoints[this.dataPoints.length - 1].timestamp;
  }
  get duration() {
    return (this.end.getTime() - this.start.getTime()) / 1e3;
  }
  removeFirstDataPoint() {
    if (this.dataPoints.length > 0) {
      this.dataPoints.shift();
    }
  }
  replaceFirstDataPoint(dataPoint) {
    this.dataPoints.unshift(dataPoint);
  }
  removeLastDataPoint() {
    if (this.dataPoints.length > 0) {
      this.dataPoints.pop();
    }
  }
  replaceLastDataPoint(dataPoint) {
    if (dataPoint) {
      this.dataPoints.push(dataPoint);
    }
  }
  insert(dataPoint) {
    this.dataPoints = this.dataPoints.filter((point) => Math.abs(point.timestamp.getTime() - dataPoint.timestamp.getTime()) >= 1e3);
    this.dataPoints.push(dataPoint);
    this.dataPoints.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  }
};

// src/utils/chart-data/chart-data-util.ts
var DEFAULT_RANGES = {
  temperature: { value: [4, 12], display: [3, 13] },
  pressure: { value: [0, 25], display: [-3, 29] },
  average_fluid_pressure: { value: [0, 65], display: [0, 65] },
  average_flow: { value: [0, 150], display: [0, 150] },
  average_resistance: { value: [0, 2], display: [0, 2] }
};
var ChartDataUtil = class _ChartDataUtil {
  static measurementTypeToUIString(type) {
    switch (type) {
      case "temperature":
        return "Temperature";
      case "pressure":
        return "Pressure";
      case "average_resistance":
        return "Avg Resistance";
      case "average_fluid_pressure":
        return "Avg Fluid Pressure";
      case "average_flow":
        return "Avg Flow";
    }
  }
  static formatMeasurementType(type, value) {
    if (value == null) {
      return "-";
    }
    switch (type) {
      case "temperature":
        return `${value.toFixed(1)} \xB0C`;
      case "pressure":
        return `${value.toFixed(1)} cmH\u20820`;
      case "average_resistance":
        if (value >= 2) {
          return "> 2 mmHg/ml/min";
        } else {
          return `${value.toFixed(1)} mmHg/ml/min`;
        }
      case "average_fluid_pressure":
        return `${Math.round(value)} mmHg`;
      case "average_flow":
        if (value === 5) {
          return "< 5 ml/min";
        } else {
          return `${value.toFixed(2)} ml/min`;
        }
      default:
        return null;
    }
  }
  static getDefaultValueRangeMedian(type) {
    const lowerBound = DEFAULT_RANGES[type].value[0];
    const upperBound = DEFAULT_RANGES[type].value[1];
    return lowerBound + (upperBound - lowerBound) / 2;
  }
  static calculateXTicks(start, end) {
    const totalTime = Math.abs(end.getTime() - start.getTime()) / 1e3;
    let interval;
    if (totalTime < 3600) {
      interval = 900 * 1e3;
    } else if (totalTime < 7200) {
      interval = 1800 * 1e3;
    } else if (totalTime < 18e3) {
      interval = 3600 * 1e3;
    } else if (totalTime < 36e3) {
      interval = 7200 * 1e3;
    } else if (totalTime < 72e3) {
      interval = 10800 * 1e3;
    } else {
      interval = Math.floor(totalTime / 5 / 3600) * 3600 * 1e3;
    }
    const ticks = [];
    for (let current = start.getTime(); current <= end.getTime(); current += interval) {
      ticks.push(current - start.getTime());
    }
    return ticks;
  }
  static formatXTick(tick) {
    const hours = Math.floor(tick / 1e3 / 3600);
    const minutes = Math.floor(tick / 1e3 % 3600 / 60);
    const formattedParts = [];
    if (hours > 0) {
      formattedParts.push(`${hours}h`);
    }
    if (minutes > 0 || hours === 0) {
      formattedParts.push(`${minutes}m`);
    }
    return formattedParts.join(" ");
  }
  static calculateYTicks(type, dataRange) {
    const preferredValueRange = _ChartDataUtil.calculatePreferredValueRange(type, dataRange);
    const numberOfValues = 5;
    const range = preferredValueRange[1] - preferredValueRange[0];
    const step = Math.floor(range / numberOfValues) + 1;
    const values = [];
    for (let value = preferredValueRange[0]; value < preferredValueRange[1]; value += step) {
      values.push(value);
    }
    if (values.length === numberOfValues) {
      values.pop();
    }
    values.push(preferredValueRange[1]);
    return values;
  }
  static calculateYDomain(type, dataRange) {
    const preferredValueRange = _ChartDataUtil.calculatePreferredValueRange(type, dataRange);
    const defaultDisplayRange = DEFAULT_RANGES[type].display;
    return [Math.min(preferredValueRange[0], defaultDisplayRange[0]), Math.max(preferredValueRange[1], defaultDisplayRange[1])];
  }
  static calculatePreferredValueRange(type, dataRange) {
    const defaultValueRange = DEFAULT_RANGES[type].value;
    return [Math.min(defaultValueRange[0], dataRange[0]), Math.max(defaultValueRange[1], dataRange[1])];
  }
};

// src/utils/chart-data/chart-data-segmentation.ts
var ChartDataSegmentation = class _ChartDataSegmentation {
  dataSegments;
  highlightPoints;
  constructor(params) {
    const dataSegments = _ChartDataSegmentation.dataSegments(params.readings);
    const dataPoints = dataSegments.flatMap((segment) => segment.dataPoints);
    const highlightPoints = _ChartDataSegmentation.highlightPoints(params.type, dataPoints, params.highlights ?? []);
    params.highlights?.forEach(({ timestamp, attributes }) => {
      if (attributes.includes("STARTS_LOGGER" /* STARTS_LOGGER */)) {
        const highlightPoint = highlightPoints.find((point) => point.timestamp.getTime() === timestamp.getTime());
        if (highlightPoint) {
          const firstSegment = dataSegments.shift();
          if (!firstSegment) {
            return;
          }
          firstSegment.replaceFirstDataPoint(highlightPoint);
          dataSegments.unshift(firstSegment);
        }
      } else if (attributes.includes("STOPS_LOGGER" /* STOPS_LOGGER */)) {
        const highlightPoint = highlightPoints.find((point) => point.timestamp.getTime() === timestamp.getTime());
        if (highlightPoint) {
          const lastSegment = dataSegments.pop();
          if (!lastSegment) {
            return;
          }
          lastSegment.replaceLastDataPoint(highlightPoint);
          dataSegments.push(lastSegment);
        }
      }
    });
    this.dataSegments = dataSegments;
    this.highlightPoints = highlightPoints;
  }
  static dataSegments(readings) {
    const dataSegments = [];
    let dataPoints = [];
    let nullSegmentPoints = [];
    for (const { timestamp, value } of readings) {
      if (value != null) {
        if (nullSegmentPoints.length > 0) {
          dataSegments.push(new ChartDataSegment({ dataPoints: nullSegmentPoints }));
          nullSegmentPoints = [];
        }
        dataPoints.push(new ChartDataPoint({ timestamp, value }));
      } else {
        if (dataPoints.length > 0) {
          dataSegments.push(new ChartDataSegment({ dataPoints }));
          dataPoints = [];
        }
        nullSegmentPoints.push(new ChartDataPoint({ timestamp, value: void 0 }));
      }
    }
    if (dataPoints.length > 0) {
      dataSegments.push(new ChartDataSegment({ dataPoints }));
    }
    if (nullSegmentPoints.length > 0) {
      dataSegments.push(new ChartDataSegment({ dataPoints: nullSegmentPoints }));
    }
    return dataSegments;
  }
  static highlightPoints(type, dataPoints, highlights) {
    if (highlights.length === 0 || dataPoints.length === 0) {
      return [];
    }
    return highlights.map((highlight) => {
      const nextIndex = dataPoints.findIndex((point) => point.timestamp.getTime() >= highlight.timestamp.getTime());
      const nextDataPoint = nextIndex !== -1 ? dataPoints[nextIndex] : void 0;
      const previousIndex = dataPoints.slice().reverse().findIndex((point) => point.timestamp.getTime() <= highlight.timestamp.getTime());
      const previousDataPoint = previousIndex !== -1 ? dataPoints[dataPoints.length - 1 - previousIndex] : void 0;
      const value = _ChartDataSegmentation.determineYValue(highlight.timestamp, previousDataPoint, nextDataPoint);
      return new ChartDataPoint({
        timestamp: highlight.timestamp,
        value: value ?? ChartDataUtil.getDefaultValueRangeMedian(type),
        isHighlight: true
      });
    });
  }
  static determineYValue(timestamp, previousDataPoint, nextDataPoint) {
    if (previousDataPoint?.value && nextDataPoint?.value) {
      return _ChartDataSegmentation.interpolateYValue(
        timestamp,
        { value: previousDataPoint.value, timestamp: previousDataPoint.timestamp },
        { value: nextDataPoint.value, timestamp: nextDataPoint.timestamp }
      );
    }
    if (nextDataPoint) {
      return nextDataPoint.value;
    }
    if (previousDataPoint) {
      return previousDataPoint.value;
    }
    return null;
  }
  static interpolateYValue(timestamp, previousDataPoint, nextDataPoint) {
    const timeDifference = nextDataPoint.timestamp.getTime() - previousDataPoint.timestamp.getTime();
    const valueDifference = nextDataPoint.value - previousDataPoint.value;
    const timeOffset = timestamp.getTime() - previousDataPoint.timestamp.getTime();
    return previousDataPoint.value + valueDifference * (timeOffset / timeDifference);
  }
};

// src/utils/chart-data/index.ts
var ChartData = class _ChartData {
  maxPoints;
  start;
  end;
  dataSegments;
  dataPoints;
  dataRange;
  constructor(params) {
    this.maxPoints = params.maxPoints ?? 300;
    this.start = params.start;
    this.end = params.end ?? /* @__PURE__ */ new Date();
    const { dataSegments, highlightPoints } = new ChartDataSegmentation({
      type: params.type,
      readings: params.readings,
      highlights: params.highlights
    });
    const UPDATE_INTERVAL = params.product === "kidneyvault" /* KIDNEYVAULT */ ? 5 : 30;
    const itemsPerSecond = 1 / UPDATE_INTERVAL;
    const duration = (this.end.getTime() - params.start.getTime()) / 1e3;
    const numberOfPoints = Math.floor(duration * itemsPerSecond);
    const segmentationFactor = this.maxPoints / numberOfPoints;
    const filteredSegments = numberOfPoints > this.maxPoints ? dataSegments.map((segment) => {
      const pointsForSegment = Math.max(2, Math.floor(segment.dataPoints.length * segmentationFactor));
      return new ChartDataSegment({ dataPoints: this.aggregate(segment.dataPoints, pointsForSegment) });
    }) : dataSegments;
    highlightPoints.forEach((highlight) => {
      const index = filteredSegments.findIndex((segment) => segment.start <= highlight.timestamp && highlight.timestamp <= segment.end);
      if (index !== -1) {
        filteredSegments[index].insert(highlight);
      } else {
        filteredSegments.push(new ChartDataSegment({ dataPoints: [highlight] }));
      }
    });
    filteredSegments.sort((a, b) => a.dataPoints[0].timestamp.getTime() - b.dataPoints[0].timestamp.getTime());
    this.dataSegments = filteredSegments;
    this.dataPoints = this.dataSegments.flatMap((segment) => segment.dataPoints);
    this.dataRange = this.calculateYAxisRange(this.dataPoints);
  }
  calculateYAxisRange(dataPoints) {
    if (dataPoints.length === 0) {
      return [0, 0];
    }
    const values = dataPoints.map((point) => point.value);
    const dataMin = Math.floor(Math.min(...values.filter(Boolean)));
    const dataMax = Math.ceil(Math.max(...values.filter(Boolean)));
    return [dataMin, dataMax];
  }
  aggregate(dataPoints, maximumPoints) {
    if (dataPoints.length === 0) {
      return [];
    }
    const dates = dataPoints.map((point) => point.timestamp);
    const bins = _ChartData.createBins(dates, maximumPoints);
    const groupedPoints = dataPoints.reduce(
      (groups, point) => {
        const index = _ChartData.findBinIndex(point.timestamp, bins);
        if (!groups[index]) {
          groups[index] = [];
        }
        groups[index].push(point);
        return groups;
      },
      {}
    );
    const sortedGroups = Object.values(groupedPoints).sort((a, b) => a[0].timestamp.getTime() - b[0].timestamp.getTime());
    return sortedGroups.map((points, index) => {
      const value = points.reduce((sum, point) => sum + (point.value ?? 0), 0) / points.filter((point) => point.value != null).length;
      let timestamp;
      if (index === 0) {
        timestamp = new Date(Math.min(...points.map((point) => point.timestamp.getTime())));
      } else if (index === sortedGroups.length - 1) {
        timestamp = new Date(Math.max(...points.map((point) => point.timestamp.getTime())));
      } else {
        timestamp = points[0].timestamp;
      }
      return new ChartDataPoint({ timestamp, value: !Number.isNaN(value) ? value : void 0 });
    });
  }
  static createBins(dates, desiredCount) {
    const sortedDates = [...dates].sort((a, b) => a.getTime() - b.getTime());
    const start = sortedDates[0];
    const end = sortedDates[sortedDates.length - 1];
    const range = end.getTime() - start.getTime();
    const binSize = range / desiredCount;
    return Array.from({ length: desiredCount }, (_, i) => new Date(start.getTime() + i * binSize));
  }
  static findBinIndex(date, bins) {
    const time = date.getTime();
    for (let i = 0; i < bins.length - 1; i++) {
      if (time >= bins[i].getTime() && time < bins[i + 1].getTime()) {
        return i;
      }
    }
    return bins.length - 1;
  }
};

// src/utils/check-user-access-permissions.ts
function checkUserAccessPermissions(user, { types, features }) {
  return Boolean(
    types && user.access?.types?.some((type) => types.includes(type)) || features && user.access?.features?.some((feature) => features.includes(feature))
  );
}

// src/utils/combine-positions.ts
function combinePositions(data) {
  const validLoggerLocations = (data.loggerLocations ?? []).filter((l) => !!l.timestamp).map((l) => ({
    latitude: l.latitude,
    longitude: l.longitude,
    timestamp: new Date(l.timestamp),
    horizontalAccuracy: l.horizontalAccuracy,
    verticalAccuracy: l.verticalAccuracy
  })).sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  const validAdvancedTracking = data.advancedTracking.filter((adv) => !!adv.timestamp).map((adv) => ({
    latitude: adv.latitude,
    longitude: adv.longitude,
    timestamp: new Date(adv.timestamp),
    horizontalAccuracy: 20,
    verticalAccuracy: 20
  })).sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  const gps = validAdvancedTracking.length > 0 ? validAdvancedTracking : validLoggerLocations;
  const validFlightPositions = data.flightPositions.filter((fp) => !!fp.timestamp).map((fp) => ({
    latitude: fp.latitude,
    longitude: fp.longitude,
    timestamp: new Date(fp.timestamp),
    horizontalAccuracy: 0,
    verticalAccuracy: 0,
    flightId: fp.flightId
  })).sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  const flightIds = Array.from(new Set(validFlightPositions.map((flightPosition) => flightPosition.flightId)));
  const flightIntervals = flightIds.map((flightId) => {
    const flightPositions = validFlightPositions.filter((flightPosition) => flightPosition.flightId === flightId);
    return {
      start: flightPositions[0].timestamp,
      end: flightPositions[flightPositions.length - 1].timestamp
    };
  });
  const combined = [
    ...gps.filter(
      (g) => !flightIntervals.some(
        (interval) => g.timestamp.getTime() >= interval.start.getTime() && g.timestamp.getTime() <= interval.end.getTime()
      )
    ),
    ...validFlightPositions
  ].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  const smoothed = smoothPolyline(combined);
  return {
    combined,
    smoothed
  };
}

// src/utils/date.ts
function toDate(value) {
  if (!value) return void 0;
  if (value instanceof Date) return value;
  if (typeof value === "object" && "toDate" in value && typeof value.toDate === "function") {
    return value.toDate();
  }
  return new Date(value);
}

// src/utils/detect-environment.ts
function detectEnvironment() {
  if (typeof navigator === "undefined") {
    return "node";
  }
  const { userAgent } = navigator;
  if (/Android/i.test(userAgent)) {
    return "android";
  }
  if (/iPhone|iPad|iPod/i.test(userAgent)) {
    return "ios";
  }
  return "web";
}

// src/utils/format-date-time.ts
import { DateTime } from "luxon";
import * as tz from "moment-timezone";
var FormatDateTimeMode = /* @__PURE__ */ ((FormatDateTimeMode2) => {
  FormatDateTimeMode2["DATE"] = "DATE";
  FormatDateTimeMode2["TIME"] = "TIME";
  FormatDateTimeMode2["DATE_TIME"] = "DATE_TIME";
  return FormatDateTimeMode2;
})(FormatDateTimeMode || {});
var DEFAULT_OPTIONS = {
  mode: "DATE_TIME" /* DATE_TIME */,
  separator: " - ",
  hour24: false,
  relativeReplace: {
    today: "Today",
    tomorrow: "Tomorrow",
    yesterday: "Yesterday"
  }
};
function formatDateTime(date, options) {
  let dateTime = DateTime.fromJSDate(date);
  const mergedOptions = { ...DEFAULT_OPTIONS, ...options ?? {} };
  dateTime = moveTimezone(dateTime, mergedOptions);
  if (mergedOptions.mode === "DATE" /* DATE */) {
    return formatDateMode(dateTime, mergedOptions);
  }
  if (mergedOptions.mode === "TIME" /* TIME */) {
    return formatTimeMode(dateTime, mergedOptions);
  }
  return formatDateTimeMode(dateTime, mergedOptions);
}
function formatDateMode(dateTime, options) {
  if (options.relativeReplace?.today && isToday(dateTime)) {
    return options.relativeReplace.today;
  }
  if (options.relativeReplace?.tomorrow && isTomorrow(dateTime)) {
    return options.relativeReplace.tomorrow;
  }
  if (options.relativeReplace?.yesterday && isYesterday(dateTime)) {
    return options.relativeReplace.yesterday;
  }
  return dateTime.toLocaleString(DateTime.DATE_SHORT).replaceAll(/\//g, ".");
}
function formatTimeMode(dateTime, options) {
  let formatted = dateTime.toFormat(options.hour24 ? "HH:mm" : "hh:mm a");
  if (options.timezone?.show) {
    const timezone = options.timezone?.offset && options.timezone?.name ? options.timezone?.name : parseTimeZone(dateTime);
    if (timezone) {
      formatted += ` (${timezone})`;
    }
  }
  return formatted;
}
function formatDateTimeMode(dateTime, options) {
  const formattedDate = formatDateMode(dateTime, options);
  const formattedTime = formatTimeMode(dateTime, options);
  return `${formattedDate}${options.separator}${formattedTime}`;
}
function parseTimeZone(dateTime) {
  const zoneName = dateTime.zoneName;
  if (!zoneName) {
    return null;
  }
  const nowInZone = DateTime.local().setZone(zoneName);
  const nowIsoString = nowInZone.toISO();
  if (!nowIsoString) {
    return null;
  }
  return tz.tz(nowIsoString, zoneName).zoneAbbr();
}
function getTimezoneAbbreviation(iana) {
  return tz.tz((/* @__PURE__ */ new Date()).toISOString(), iana).zoneAbbr();
}
function isToday(dateTime) {
  return dateTime.toISODate() === DateTime.local().toISODate();
}
function isTomorrow(dateTime) {
  return dateTime.toISODate() === DateTime.local().plus({ days: 1 }).toISODate();
}
function isYesterday(dateTime) {
  return dateTime.toISODate() === DateTime.local().minus({ days: 1 }).toISODate();
}
function moveTimezone(dateTime, options) {
  if (options.timezone?.iana) {
    return dateTime.setZone(options.timezone.iana);
  }
  const iana = getIANA();
  if (iana) {
    return dateTime.setZone(iana);
  }
  if (!options.timezone?.offset) {
    return dateTime;
  }
  const { zoneName } = DateTime.fromISO(`2020-01-01T01:00:00${options.timezone.offset}`, { setZone: true });
  if (!zoneName) {
    return dateTime;
  }
  return dateTime.setZone(zoneName);
}
function getIANA() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (err) {
    return null;
  }
}

// src/utils/format-number.ts
function formatNumber(value, fractionDigits = 0) {
  return Intl.NumberFormat("en-US", { maximumFractionDigits: fractionDigits }).format(value);
}

// src/utils/format-distance.ts
function formatDistance(distance, unit = "metric" /* METRIC */, options = {}) {
  const _options = { fractionDigits: 0, includeUnit: true, ...options };
  switch (unit) {
    case "metric" /* METRIC */:
      return formatKm(distance, _options);
    case "imperial" /* IMPERIAL */:
      return formatMiles(distance, _options);
    case "nautical" /* NAUTICAL */:
      return formatNauticalMiles(distance, _options);
    default:
      return formatKm(distance, _options);
  }
}
function formatKm(distance, options) {
  if (distance < 1) {
    return `${formatNumber(Math.round(distance * 1e3))}${options.includeUnit ? " m" : ""}`;
  }
  return `${formatNumber(distance, options.fractionDigits)}${options.includeUnit ? " km" : ""}`;
}
function formatMiles(distance, options) {
  const miles = distance / 1.609344;
  if (miles < 1) {
    const feet = miles * 5280;
    return `${formatNumber(Math.round(feet))}${options.includeUnit ? " ft" : ""}`;
  }
  return `${formatNumber(miles, options.fractionDigits)}${options.includeUnit ? " mi" : ""}`;
}
function formatNauticalMiles(distance, options) {
  const nauticalMiles = distance * 0.539957;
  return `${formatNumber(nauticalMiles, options.fractionDigits)}${options.includeUnit ? " nmi" : ""}`;
}

// src/utils/format-flow.ts
function formatFlow(flow, options = {}) {
  const _options = { fractionDigits: 0, includeUnit: true, ...options };
  return `${(Math.ceil(flow * 100) / 100).toFixed(_options.fractionDigits)} ${_options.includeUnit ? "ml/min" : ""}`.trim();
}

// src/utils/format-fluid-pressure.ts
function formatFluidPressure(fluidPressure, options = {}) {
  const _options = { fractionDigits: 0, includeUnit: true, ...options };
  return `${(Math.ceil(fluidPressure * 100) / 100).toFixed(_options.fractionDigits)} ${_options.includeUnit ? "mmHg" : ""}`.trim();
}

// src/utils/format-height.ts
var CM_PER_INCH = 2.54;
var INCHES_PER_FOOT = 12;
function cmToFeetInches(cm) {
  const totalInches = cm / CM_PER_INCH;
  let feet = Math.floor(totalInches / INCHES_PER_FOOT);
  let inches = Math.round(totalInches - feet * INCHES_PER_FOOT);
  if (inches === INCHES_PER_FOOT) {
    feet += 1;
    inches = 0;
  }
  return { feet, inches };
}
function feetInchesToCm(feet, inches) {
  return (feet * INCHES_PER_FOOT + inches) * CM_PER_INCH;
}
function formatHeight(cm, unit = "metric" /* METRIC */) {
  if (unit === "imperial" /* IMPERIAL */) {
    const { feet, inches } = cmToFeetInches(cm);
    return `${feet} ft, ${inches} in`;
  }
  return `${Math.round(cm)} cm`;
}

// src/utils/format-pressure.ts
function formatPressure(pressure, options = {}) {
  const _options = { fractionDigits: 0, includeUnit: true, ...options };
  return `${(Math.ceil(pressure * 100) / 100).toFixed(_options.fractionDigits)} ${_options.includeUnit ? "cmH\u20820" : ""}`.trim();
}

// src/utils/format-resistance.ts
function formatResistance(resistance, options = {}) {
  const _options = { fractionDigits: 0, includeUnit: true, ...options };
  return `${(Math.ceil(resistance * 100) / 100).toFixed(_options.fractionDigits)} ${_options.includeUnit ? "mmHg/ml/min" : ""}`.trim();
}

// src/utils/format-temperature.ts
function formatTemperature(temperature, options = {}) {
  const _options = { fractionDigits: 0, includeUnit: true, ...options };
  return `${temperature.toFixed(_options.fractionDigits)}${_options.includeUnit ? "\xB0C" : ""}`;
}

// src/utils/format-weight.ts
var LBS_PER_KG = 2.2046226218;
function kgToLbs(kg) {
  return kg * LBS_PER_KG;
}
function lbsToKg(lbs) {
  return lbs / LBS_PER_KG;
}
function formatWeight(kg, unit = "metric" /* METRIC */) {
  if (unit === "imperial" /* IMPERIAL */) {
    return `${Math.round(kgToLbs(kg))} lbs`;
  }
  return `${Math.round(kg)} kg`;
}

// src/utils/get-interval-logs.ts
import { differenceInMinutes } from "date-fns";
import { DateTime as DateTime2 } from "luxon";
function getIntervalLogs(logs, timeline, interval, timezone) {
  const readings = logs.filter((l) => l.isReading && l.timestamp);
  if (readings.length === 0) {
    return [];
  }
  const highlights = timeline.map((entry) => ({ timestamp: entry.timestamp, attributes: TimelineEntryTypeUtil.attributes(entry.type) })).filter((entry) => entry.attributes.length > 0);
  const startsLogger = highlights?.find((h) => h.attributes.includes("STARTS_LOGGER" /* STARTS_LOGGER */))?.timestamp ?? readings[0]?.timestamp;
  const stopsLogger = highlights?.find((h) => h.attributes.includes("STOPS_LOGGER" /* STOPS_LOGGER */))?.timestamp ?? readings[readings.length - 1]?.timestamp;
  const intervalEntries = [];
  if (startsLogger && stopsLogger) {
    const firstReading = readings[0];
    if (firstReading) {
      intervalEntries.push({ targetTime: new Date(firstReading.timestamp), reading: firstReading });
    }
    let currentTarget = new Date(startsLogger);
    const minutesToAdd = interval - currentTarget.getMinutes() % interval;
    if (minutesToAdd !== interval) {
      currentTarget.setMinutes(currentTarget.getMinutes() + minutesToAdd, 0, 0);
    } else {
      currentTarget.setSeconds(0, 0);
    }
    while (currentTarget.getTime() < stopsLogger.getTime()) {
      const windowSizeMs = Math.floor(interval * 60 * 1e3 * 0.25);
      const startRange = currentTarget.getTime() - windowSizeMs;
      const endRange = currentTarget.getTime() + windowSizeMs;
      let closestReading = null;
      let closestTimeDiff = Infinity;
      for (const reading of readings) {
        const readingTime = reading.timestamp.getTime();
        if (readingTime >= startRange && readingTime <= endRange) {
          const timeDiff = Math.abs(readingTime - currentTarget.getTime());
          if (timeDiff < closestTimeDiff) {
            closestTimeDiff = timeDiff;
            closestReading = reading;
          }
        }
      }
      if (closestReading) {
        const alreadyUsed = intervalEntries.some((entry) => entry.reading.timestamp.getTime() === closestReading.timestamp.getTime());
        if (!alreadyUsed) {
          intervalEntries.push({
            targetTime: new Date(currentTarget),
            reading: closestReading
          });
        }
      }
      currentTarget = new Date(currentTarget);
      currentTarget.setMinutes(currentTarget.getMinutes() + interval);
    }
    const lastReading = readings[readings.length - 1];
    if (lastReading && (!intervalEntries.length || lastReading.timestamp.getTime() !== intervalEntries[intervalEntries.length - 1].reading.timestamp.getTime())) {
      intervalEntries.push({ targetTime: new Date(lastReading.timestamp), reading: lastReading });
    }
  }
  const firstTimestamp = intervalEntries[0]?.targetTime;
  return intervalEntries.map((entry) => {
    let durationStr = "-";
    if (entry.targetTime && firstTimestamp) {
      const diffMinsTotal = differenceInMinutes(entry.targetTime, firstTimestamp);
      const days = Math.floor(diffMinsTotal / (60 * 24));
      const hours = Math.floor(diffMinsTotal % (60 * 24) / 60);
      const minutes = diffMinsTotal % 60;
      let parts = [];
      if (days > 0) parts.push(`${days}d`);
      if (hours > 0) parts.push(`${hours}h`);
      if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`);
      durationStr = parts.join(" ");
    }
    const formattedTime = entry.targetTime ? DateTime2.fromJSDate(entry.targetTime).setZone(timezone).toFormat("HH:mm") : "-";
    return {
      duration: durationStr,
      time: formattedTime,
      pressure: entry.reading.data.averageFluidPressure?.toFixed(0) ?? "-",
      resistance: entry.reading.data.averageResistance?.toFixed(2) ?? "-",
      flow: entry.reading.data.averageFlow?.toFixed(0) ?? "-",
      temp: entry.reading.data.probe?.toFixed(1) ?? "-",
      timestamp: entry.reading.timestamp?.toISOString()
    };
  });
}

// src/utils/inventory/audit-period.ts
import { DateTime as DateTime3 } from "luxon";
var AUDIT_TIME_ZONE = "America/Los_Angeles";
function getAuditPeriodFromDate(date) {
  const zoned = DateTime3.fromJSDate(date, { zone: AUDIT_TIME_ZONE });
  return `${zoned.year}Q${zoned.quarter}`;
}
function getAuditPeriodEndDate(period) {
  const match = period.match(/^(\d{4})Q([1-4])$/);
  if (!match) throw new Error(`getAuditPeriodEndDate: malformed period "${period}", expected "YYYYQ[1-4]"`);
  const year = Number(match[1]);
  const quarter = Number(match[2]);
  const firstMonthOfQuarter = quarter * 3 - 2;
  return DateTime3.fromObject({ year, month: firstMonthOfQuarter, day: 1 }, { zone: AUDIT_TIME_ZONE }).endOf("quarter").toJSDate();
}
function isAuditPeriodOverdue(period, now = /* @__PURE__ */ new Date()) {
  if (!period || !/^\d{4}Q[1-4]$/.test(period)) return false;
  return now.getTime() > getAuditPeriodEndDate(period).getTime();
}

// src/utils/inventory/barcode.ts
import { format, isValid, parse } from "date-fns";
var GS1_128_PATTERN = /^01(?<gtin>.{14})11(?<mfgDate>.{6})17(?<expDate>.{6})10(?<lotNumber>(?:[a-zA-Z0-9]{2,3}\d{6}|\d{2}-\d{4}))(?:21(?<serialNumber>.+))?$/;
function createBarcode(gtin, manufacturingDate, expirationDate, serialNumber, componentCode, sequenceNumber) {
  const mfgDateStr = format(manufacturingDate, "yyMMdd");
  const expDateStr = format(expirationDate, "yyMMdd");
  const lotNumber = generateLotNumber(componentCode, manufacturingDate, sequenceNumber);
  const baseBarcode = `01${gtin}11${mfgDateStr}17${expDateStr}10${lotNumber}`;
  return serialNumber ? `${baseBarcode}21${serialNumber}` : baseBarcode;
}
function parseBarcode(barcode) {
  const match = barcode.match(GS1_128_PATTERN);
  if (!match || !match.groups) {
    throw new Error(`Barcode does not match expected GS1-128 pattern: ${barcode}`);
  }
  const { gtin, mfgDate, expDate, lotNumber, serialNumber } = match.groups;
  if (!gtin || !mfgDate || !expDate || !lotNumber) {
    throw new Error(`Barcode parsing failed: Missing required components. Original: ${barcode}`);
  }
  if (!serialNumber) {
    throw new Error(`Barcode parsing failed: Serial number is required for inventory tracking. Original: ${barcode}`);
  }
  let manufacturingDate;
  let expirationDate;
  try {
    manufacturingDate = parse(mfgDate, "yyMMdd", /* @__PURE__ */ new Date());
    expirationDate = parse(expDate, "yyMMdd", /* @__PURE__ */ new Date());
  } catch (dateError) {
    throw new Error(`Invalid date format in barcode: ${dateError.message}. Original: ${barcode}`);
  }
  if (!isValid(manufacturingDate) || !isValid(expirationDate)) {
    throw new Error(`Invalid date component found after parsing. Mfg: ${mfgDate}, Exp: ${expDate}`);
  }
  const componentCode = extractComponentCodeFromLotNumber(lotNumber);
  return {
    gtin,
    manufacturingDate,
    expirationDate,
    lotNumber,
    serialNumber,
    componentCode
  };
}
var GS = "";
function encodeGS1Barcode(barcode) {
  const match = barcode.match(GS1_128_PATTERN);
  if (!match || !match.groups) return barcode;
  const { gtin, mfgDate, expDate, lotNumber, serialNumber } = match.groups;
  const base = `01${gtin}11${mfgDate}17${expDate}10${lotNumber}`;
  return serialNumber ? `${base}${GS}21${serialNumber}` : base;
}
function formatBarcodeHumanReadable(barcode) {
  const match = barcode.match(GS1_128_PATTERN);
  if (!match || !match.groups) return barcode;
  const { gtin, mfgDate, expDate, lotNumber, serialNumber } = match.groups;
  let formatted = `(01)${gtin}(11)${mfgDate}(17)${expDate}(10)${lotNumber}`;
  if (serialNumber) {
    formatted += `(21)${serialNumber}`;
  }
  return formatted;
}
var STERILE_GOODS_COMPONENT_CODE = "BS";
function extractComponentCodeFromLotNumber(lotNumber) {
  if (isSterileGoodsLotNumber(lotNumber)) {
    return STERILE_GOODS_COMPONENT_CODE;
  }
  return lotNumber.slice(0, lotNumber.length - 6);
}
function generateLotNumber(componentCode, manufacturingDate, sequenceNumber) {
  if (isSterileGoodsComponent(componentCode)) {
    return generateSterileGoodsLotNumber(manufacturingDate, sequenceNumber);
  } else {
    const mfgDateStr = format(manufacturingDate, "yyMMdd");
    return `${componentCode}${mfgDateStr}`;
  }
}
function generateSterileGoodsLotNumber(manufacturingDate, sequenceNumber) {
  const year = format(manufacturingDate, "yy");
  const sequence = sequenceNumber || Math.floor(Math.random() * 1e4).toString().padStart(4, "0");
  return `${year}-${sequence}`;
}
function isSterileGoodsComponent(componentCode) {
  return componentCode === STERILE_GOODS_COMPONENT_CODE;
}
function isSterileGoodsLotNumber(lotNumber) {
  return lotNumber.includes("-");
}

// src/utils/milliseconds-to-time.ts
function millisecondsToTime(milliseconds, options) {
  if (milliseconds == null || Number.isNaN(milliseconds)) {
    return "-";
  }
  const [days, hours, minutes, seconds] = millisecondsToTimeArray(milliseconds);
  return [
    days > 0 && options?.days !== false ? `${new Intl.NumberFormat().format(days)}d` : null,
    hours > 0 && options?.hours !== false ? `${new Intl.NumberFormat().format(hours)}h` : null,
    minutes > 0 && options?.minutes !== false ? `${new Intl.NumberFormat().format(minutes)}m` : null,
    seconds > 0 && options?.seconds !== false ? `${new Intl.NumberFormat().format(seconds)}s` : null
  ].filter((elem) => elem != null).slice(options?.minUnits != null ? options.minUnits : 0, options?.maxUnits != null ? options.maxUnits : options?.maxUnits).join(" ");
}

// src/utils/resolve-location.ts
var STALE_THRESHOLD_MS = 5 * 60 * 1e3;
function getTimestamp(source) {
  if (!source?.timestamp) return null;
  return source.timestamp instanceof Date ? source.timestamp : new Date(source.timestamp);
}
function isNewerByThreshold(newer, older) {
  return newer.getTime() - older.getTime() > STALE_THRESHOLD_MS;
}
function resolveLocation(sources) {
  const flightTs = getTimestamp(sources.flightPosition);
  const vantageTs = getTimestamp(sources.advancedTracking);
  const appTs = getTimestamp(sources.loggerLocation);
  const flight = sources.flightPosition && flightTs ? { ...sources.flightPosition, ts: flightTs } : null;
  const vantage = sources.advancedTracking && vantageTs ? { ...sources.advancedTracking, ts: vantageTs } : null;
  const app = sources.loggerLocation && appTs ? { ...sources.loggerLocation, ts: appTs } : null;
  if (flight) {
    if (app && isNewerByThreshold(app.ts, flight.ts)) {
      return { latitude: app.latitude, longitude: app.longitude, timestamp: app.ts, sourceType: "app" };
    }
    if (vantage && isNewerByThreshold(vantage.ts, flight.ts)) {
      return { latitude: vantage.latitude, longitude: vantage.longitude, timestamp: vantage.ts, sourceType: "vantagePoint" };
    }
    return { latitude: flight.latitude, longitude: flight.longitude, timestamp: flight.ts, sourceType: "flight", heading: flight.heading };
  }
  if (app) {
    if (vantage && isNewerByThreshold(vantage.ts, app.ts)) {
      return { latitude: vantage.latitude, longitude: vantage.longitude, timestamp: vantage.ts, sourceType: "vantagePoint" };
    }
    return { latitude: app.latitude, longitude: app.longitude, timestamp: app.ts, sourceType: "app" };
  }
  if (vantage) {
    return { latitude: vantage.latitude, longitude: vantage.longitude, timestamp: vantage.ts, sourceType: "vantagePoint" };
  }
  return null;
}

// src/admin/collections.ts
var COLLECTIONS = {
  /** Geographic areas for territory management */
  AREAS: "areas",
  /** Active organ transport sessions */
  SESSIONS: "sessions",
  /** Publicly shared session data (read-only snapshots) */
  SHARED_SESSIONS: "shared_sessions",
  /** Organization/tenant data */
  ORGANIZATIONS: "organizations",
  /** User accounts and profiles */
  USERS: "users",
  /** Geographic territories containing multiple areas */
  TERRITORIES: "territories",
  /** Product catalog (e.g., devices, accessories) */
  PRODUCTS: "products",
  /** Physical inventory storage locations */
  INVENTORY_LOCATIONS: "inventory_locations",
  /** Individual inventory components (serialized items) */
  INVENTORY_COMPONENTS: "inventory_components",
  /** Inventory movement and state change transactions */
  INVENTORY_TRANSACTIONS: "inventory_transactions",
  /** Unregistered user invitations to organizations */
  ORGANIZATIONS_UNREGISTERED_INVITES: "organizations_unregistered_invites",
  /** Temporary destination data (used during onboarding/setup) */
  DESTINATIONS_TMP: "destinationsTmp",
  /** System configuration and reference data */
  CONFIG: "config",
  /** Temporary offer drafts for PDF upload and extraction */
  DRAFTS: "drafts",
  /** Centralized logger data for v7+ sessions */
  LOGGERS: "loggers"
};
var SUBCOLLECTIONS = {
  /**
   * Subcollections under the `sessions` collection
   */
  SESSIONS: {
    /** File attachments associated with a session */
    ATTACHMENTS: "attachments",
    /** Chat threads for session collaboration */
    CHATS: "chats",
    /** Ischemic time tracking records */
    ISCHEMIC_TIMES: "ischemicTimes",
    /** Organ data within a session (multi-organ support) */
    ORGANS: "organs",
    /** Timeline events and milestones */
    TIMELINE: "timeline",
    /** Flight information for transport */
    FLIGHTS: "flights",
    /** Location history and waypoints */
    LOCATIONS: "locations",
    /** Temperature and environmental readings */
    READINGS: "readings",
    /** Users associated with the session */
    USERS: "users",
    /** Data access audit logs */
    DATA_ACCESS_LOGS: "dataAccessLogs"
  },
  /**
   * Subcollections under the `chats` subcollection
   */
  CHATS: {
    /** Individual messages within a chat thread */
    MESSAGES: "messages",
    /** Members of a chat (user references) */
    MEMBERS: "members"
  },
  /**
   * Subcollections under the `messages` subcollection
   */
  MESSAGES: {
    /** Read receipts for messages */
    RECEIPTS: "receipts"
  },
  /**
   * Subcollections under the `timeline` subcollection
   */
  TIMELINE: {
    /** Comments on a timeline entry */
    COMMENTS: "comments"
  },
  /**
   * Subcollections under the `comments` subcollection
   */
  COMMENTS: {
    /** Read receipts for comments */
    RECEIPTS: "receipts"
  },
  /**
   * Subcollections under the `shared_sessions` collection
   */
  SHARED_SESSIONS: {
    /** Organ data snapshots in shared sessions */
    ORGANS: "organs"
  },
  /**
   * Subcollections under the `organizations` collection
   */
  ORGANIZATIONS: {
    /** Organization members (registered users) */
    USERS: "users",
    /** Pre-approved users awaiting registration */
    PRESELECTED_USERS: "preselectedUsers",
    /** Pending invitations to unregistered users */
    UNREGISTERED_INVITES: "unregisteredInvites"
  },
  /**
   * Subcollections under the `users` collection
   */
  USERS: {
    /** Push notification tokens for mobile devices */
    PUSH_TOKENS: "pushTokens",
    /** User chat metadata and last-read timestamps (`/users/{userId}/chats/{chatId}`) */
    USER_CHATS: "chats",
    /** User's registered devices */
    DEVICES: "devices"
  },
  /**
   * Subcollections under the `products` collection
   */
  PRODUCTS: {
    /** Product documentation files */
    DOCUMENTS: "documents",
    /** Product tutorial videos and guides */
    TUTORIALS: "tutorials"
  },
  /**
   * Subcollections under the `inventory_locations` collection
   */
  INVENTORY_LOCATIONS: {
    /** Devices stored at an inventory location */
    DEVICES: "devices"
  },
  /**
   * Subcollections under the `inventory_components` collection
   */
  INVENTORY_COMPONENTS: {
    /** State change history for audit trail */
    STATE_HISTORY: "state_history"
  },
  /**
   * Subcollections under the `inventory_transactions` collection
   */
  INVENTORY_TRANSACTIONS: {
    /** Transaction history and audit log */
    HISTORY: "history"
  },
  /**
   * Subcollections under the `config` collection
   */
  CONFIG: {
    /** Hospital and transplant center destinations */
    DESTINATIONS: "destinations",
    /** Organization configuration options */
    ORGANIZATION_OPTIONS: "organization_options",
    /** Organization types and classifications */
    TYPES: "types",
    /** User role definitions */
    USER_ROLES: "user_roles"
  },
  /**
   * Subcollections under the `loggers` collection (v7+)
   */
  LOGGERS: {
    /** Paginated sensor log entries (500 entries per page) */
    APP_LOGS: "appLogs",
    /** GPS location data from paired mobile device */
    APP_LOCATIONS: "appLocations",
    /** Barometer readings from paired mobile device */
    APP_PRESSURES: "appPressures",
    /** Pre-processed chart data for visualization */
    CHART: "chart",
    /** Advanced tracking (Tive) location data */
    VANTAGE_POINTS: "vantagePoints",
    /** Retained advanced tracking locations (backup on unlink) */
    RETAINED_VANTAGE_POINTS: "retained_vantage_points",
    /** Retained app logs (backup on unlink) */
    RETAINED_APP_LOGS: "retained_logs"
  }
};

// src/utils/session-document-descriptor.ts
function parseDocumentDescriptor(path) {
  const parts = path.split("/").filter(Boolean);
  const sessionsIndex = parts.indexOf(COLLECTIONS.SESSIONS);
  if (sessionsIndex < 0) {
    return void 0;
  }
  const segments = parts.slice(sessionsIndex + 1);
  if (segments.length === 5 && segments[1] === SUBCOLLECTIONS.SESSIONS.CHATS && segments[3] === SUBCOLLECTIONS.CHATS.MESSAGES) {
    return {
      type: "chatMessage",
      sessionId: segments[0],
      chatId: segments[2],
      messageId: segments[4]
    };
  }
  if (segments.length === 5 && segments[1] === SUBCOLLECTIONS.SESSIONS.TIMELINE && segments[3] === SUBCOLLECTIONS.TIMELINE.COMMENTS) {
    return {
      type: "timelineComment",
      sessionId: segments[0],
      entryId: segments[2],
      commentId: segments[4]
    };
  }
  if (segments.length === 3 && segments[1] === SUBCOLLECTIONS.SESSIONS.TIMELINE) {
    return {
      type: "timelineEntry",
      sessionId: segments[0],
      entryId: segments[2]
    };
  }
  return void 0;
}
function draftPath(draftId) {
  return `${COLLECTIONS.DRAFTS}/${draftId}`;
}
function sessionPath(sessionId) {
  return `${COLLECTIONS.SESSIONS}/${sessionId}`;
}
function chatMessagePath(sessionId, chatId, messageId) {
  return `${COLLECTIONS.SESSIONS}/${sessionId}/${SUBCOLLECTIONS.SESSIONS.CHATS}/${chatId}/${SUBCOLLECTIONS.CHATS.MESSAGES}/${messageId}`;
}
function chatMessagesPath(sessionId, chatId) {
  return `${COLLECTIONS.SESSIONS}/${sessionId}/${SUBCOLLECTIONS.SESSIONS.CHATS}/${chatId}/${SUBCOLLECTIONS.CHATS.MESSAGES}`;
}
function timelineCommentPath(sessionId, entryId, commentId) {
  return `${COLLECTIONS.SESSIONS}/${sessionId}/${SUBCOLLECTIONS.SESSIONS.TIMELINE}/${entryId}/${SUBCOLLECTIONS.TIMELINE.COMMENTS}/${commentId}`;
}
function timelineCommentsPath(sessionId, entryId) {
  return `${COLLECTIONS.SESSIONS}/${sessionId}/${SUBCOLLECTIONS.SESSIONS.TIMELINE}/${entryId}/${SUBCOLLECTIONS.TIMELINE.COMMENTS}`;
}
export {
  ChartData,
  ChartDataUtil,
  FormatDateTimeMode,
  batchAsync,
  calculateChartTicks,
  calculateLoggerDistance,
  chatMessagePath,
  chatMessagesPath,
  checkUserAccessPermissions,
  cmToFeetInches,
  combinePositions,
  createBarcode,
  detectEnvironment,
  draftPath,
  encodeGS1Barcode,
  feetInchesToCm,
  formatBarcodeHumanReadable,
  formatDateTime,
  formatDistance,
  formatDuration,
  formatFlow,
  formatFluidPressure,
  formatHeight,
  formatNumber,
  formatPressure,
  formatResistance,
  formatTemperature,
  formatWeight,
  getAuditPeriodEndDate,
  getAuditPeriodFromDate,
  getDefaultMeasurementUnit,
  getIntervalLogs,
  getTimezoneAbbreviation,
  isAuditPeriodOverdue,
  kgToLbs,
  lbsToKg,
  millisecondsToTime,
  millisecondsToTimeArray,
  parseBarcode,
  parseDocumentDescriptor,
  resolveLocation,
  sessionPath,
  smoothPolyline,
  timelineCommentPath,
  timelineCommentsPath,
  toDate
};
